import { defineStore } from 'pinia';
import { getUsrInfo } from '../api/auth';
import { safeStorage } from '../utils/safeLocalStorage';

// 用户信息接口
export interface UserInfo {
  id?: string;
  username?: string;
  nickname?: string;
  avatar?: string;
  sysUser?: any;
  permissions?: string[];
  [key: string]: any;
}

// 定义全局 actions 类型
export interface GlobalActions {
  setUserInfo: (userInfo: UserInfo | null) => void;
  setToken: (token: string | null) => void;
  clearAuth: () => void;
  getUserInfo: () => UserInfo | null;
  getToken: () => string | null;
  getAuthList: () => string[];
  isAuthenticated: () => boolean;
  onStateChange: (callback: (state: any) => void) => void;
  offStateChange: (callback: (state: any) => void) => void;
}

export const useUserStore = defineStore('user', {
  state: () => ({
    token: null as string | null,
    userInfo: null as UserInfo | null,
    authList: [] as string[],
    isLoading: false,
    stateChangeListeners: new Set<(state: any) => void>()
  }),

  getters: {
    isAuthenticated: state => !!state.token,
    userPermissions: state => state.userInfo?.permissions || [],
    tenantId: state => state.userInfo?.sysUser?.tenantId || null
    // getAuthList: state => state.authList
  },

  actions: {
    notifyStateChange() {
      const state = {
        token: this.token,
        userInfo: this.userInfo,
        isLoading: this.isLoading,
        isAuthenticated: this.isAuthenticated
      };

      this.stateChangeListeners.forEach(callback => {
        try {
          callback(state);
        } catch (error) {
          console.error('状态变化回调执行失败:', error);
        }
      });

      // 触发全局事件，用于 qiankun actions
      window.dispatchEvent(
        new CustomEvent('auth-state-changed', {
          detail: { state }
        })
      );
    },

    setToken(newToken: string | null) {
      this.token = newToken;
      if (newToken) {
        safeStorage.set('auth_token', newToken);
        localStorage.setItem('auth_token', newToken);
        localStorage.setItem('token', newToken);
        localStorage.setItem('TOKEN', newToken);
      } else {
        safeStorage.remove('auth_token');
      }
      this.notifyStateChange();
    },

    // 仅设置内存token，不保存到storage（用于登录过程中的临时token）
    setTempToken(newToken: string | null) {
      this.token = newToken;
      this.notifyStateChange();
    },

    setUserInfo(newUserInfo: UserInfo | null) {
      this.userInfo = newUserInfo;
      if (newUserInfo) {
        // 同时保存到内存(pinia)和本地缓存(safeStorage)
        safeStorage.set('user_info', newUserInfo);
        localStorage.setItem('user_info', JSON.stringify(newUserInfo));
        this.saveUserInfoToLocalStorage(newUserInfo);
      } else {
        safeStorage.remove('user_info');
      }
      this.notifyStateChange();
    },

    setLoading(loading: boolean) {
      this.isLoading = loading;
      this.notifyStateChange();
    },

    setAuthList(authList: string[]): void {
      this.authList = authList;
      safeStorage.set('authList', authList);
      localStorage.setItem('authList', JSON.stringify(authList));
    },

    getAuthList() {
      if (this.authList.length === 0) {
        const cachedAuthList = safeStorage.get<string[]>('authList');
        if (cachedAuthList) {
          this.authList = cachedAuthList;
        }
      }
      return this.authList;
    },

    clearAuth() {
      this.token = null;
      this.userInfo = null;
      this.authList = [];
      this.isLoading = false;

      safeStorage.clearForCurrentUser();
      safeStorage.setCurrentUser(null);

      this.notifyStateChange();
    },

    async fetchUserInfo() {
      if (!this.token) return null;

      try {
        this.setLoading(true);
        const response = await getUsrInfo();
        const userData = response?.data as UserInfo;

        // 设置当前用户ID到safeStorage
        const userId = userData?.sysUser?.userId;
        if (userId) {
          safeStorage.setCurrentUser(userId);
        }
        //设置 tenantId
        const tenantId = userData?.sysUser?.tenantId;
        if (tenantId) {
          localStorage.setItem('tenantId', tenantId);
        }

        // 保存用户信息
        this.userInfo = userData;
        if (userData) {
          safeStorage.set('user_info', userData);
          localStorage.setItem('user_info', JSON.stringify(userData));
          this.saveUserInfoToLocalStorage(userData);
        }
        this.notifyStateChange();

        return userData;
      } catch (error) {
        console.error('获取用户信息失败:', error);
        this.clearAuth();
        return null;
      } finally {
        this.setLoading(false);
      }
    },

    saveUserInfoToLocalStorage(info: UserInfo | null) {
      if (info) {
        const permissions = info.permissions || [];
        let tmp = [];
        permissions.forEach(item => {
          tmp.push(item);
        });
        tmp.push('detail');
        this.setAuthList(tmp);

        // 使用safeStorage保存tenantId
        const tenantId = info.sysUser?.tenantId || '';
        safeStorage.set('tenantId', tenantId);
        localStorage.setItem('tenantId', tenantId);
      }
    },

    initializeAuth() {
      console.log('🔄 初始化认证状态...');

      // 尝试从缓存恢复数据
      let cachedToken = safeStorage.get<string>('auth_token');
      let cachedUserInfo = safeStorage.get<UserInfo>('user_info');

      // 如果没有找到数据，可能是用户隔离的问题，尝试查找所有可能的用户数据
      if (!cachedToken || !cachedUserInfo) {
        const allKeys = Object.keys(localStorage).filter(key => key.startsWith('safe_storage_'));

        // 尝试找到用户相关的数据
        for (const key of allKeys) {
          if (key.includes('_user_info')) {
            try {
              const itemStr = localStorage.getItem(key);
              if (itemStr) {
                const item = JSON.parse(itemStr);

                // 如果找到了用户信息，先提取userId
                if (item.value?.sysUser?.userId) {
                  const userId = item.value.sysUser.userId;
                  safeStorage.setCurrentUser(userId);

                  // 重新尝试读取数据
                  cachedToken = safeStorage.get<string>('auth_token');
                  cachedUserInfo = safeStorage.get<UserInfo>('user_info');
                  break;
                }
              }
            } catch (error) {
              console.warn(`解析key ${key} 失败:`, error);
            }
          }
        }
      }

      // 恢复token
      if (cachedToken && !this.token) {
        this.token = cachedToken;
      }

      // 恢复用户信息
      if (cachedUserInfo && !this.userInfo) {
        this.userInfo = cachedUserInfo;

        // 确保userId上下文正确设置
        const userId = cachedUserInfo?.sysUser?.userId;
        if (userId && safeStorage.getCurrentUser() !== userId) {
          safeStorage.setCurrentUser(userId);
        }
      }

      // 恢复权限列表
      if (this.authList.length === 0) {
        const cachedAuthList = safeStorage.get<string[]>('authList');
        if (cachedAuthList) {
          this.authList = cachedAuthList;
        }
      }

      console.log('✅ 认证状态初始化完成:', {
        hasToken: !!this.token,
        hasUserInfo: !!this.userInfo,
        userName: this.userInfo?.sysUser?.name,
        isAuthenticated: this.isAuthenticated
      });

      // 触发状态变化通知
      this.notifyStateChange();

      // 如果有token但没有userInfo，尝试重新获取用户信息
      if (this.token && !this.userInfo) {
        setTimeout(() => {
          this.fetchUserInfo().catch(error => {
            console.error('重新获取用户信息失败:', error);
          });
        }, 100);
      }
    },

    onStateChange(callback: (state: any) => void) {
      this.stateChangeListeners.add(callback);
      // 立即回调当前状态
      callback({
        token: this.token,
        userInfo: this.userInfo,
        isLoading: this.isLoading,
        isAuthenticated: this.isAuthenticated
      });
    },

    offStateChange(callback: (state: any) => void) {
      this.stateChangeListeners.delete(callback);
    },

    createGlobalActions(): GlobalActions {
      return {
        setUserInfo: this.setUserInfo.bind(this),
        setToken: this.setToken.bind(this),
        clearAuth: this.clearAuth.bind(this),
        getUserInfo: () => this.userInfo,
        getToken: () => this.token,
        getAuthList: () => this.authList,
        isAuthenticated: () => this.isAuthenticated,
        onStateChange: this.onStateChange.bind(this),
        offStateChange: this.offStateChange.bind(this)
      };
    },

    // 添加获取token的方法
    getToken() {
      return this.token;
    }
  }
});
